import { useFetch } from 'hooks';
import { useRef, useCallback } from 'react';
import {
  ApiOkResponse,
  MappedError,
  defaultErrorMapper,
  isAbortError,
  readBodySafe,
  throwApiError,
} from 'types/common/ApiHelpers';
import {
  ApplicationFormOption,
  ApplicationFormOptionsMap,
} from 'types/responses/sign-up/ApplicationFormOptionsResponse';
import {
  MerchantApplicationStatus,
  MerchantApplicationProgressResponse,
  MerchantApplicationResponse,
  IdentityDocumentType,
} from 'types/responses/activation';
import { logError } from './logger';
import { PageAction } from 'enums';
import {
  normaliseAIPrefilling,
  type StoredActivationData,
} from 'modules/activation/activationStorage';
export const FINAL_STAGES: MerchantApplicationStatus[] = [
  MerchantApplicationStatus.Submitted,
  MerchantApplicationStatus.Approved,
  MerchantApplicationStatus.Declined,
  MerchantApplicationStatus.Onboarded,
];

export interface BaseIdentitySuggestionsResponse {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  date_of_birth?: string;
  address?: string;
  application_id?: string;
}

export interface DriversLicenceSuggestionsResponse
  extends BaseIdentitySuggestionsResponse {
  licence_number?: string;
  card_number?: string;
  state_or_country?: string;
  dl_digital?: boolean;
  has_front?: boolean;
  has_back?: boolean;
  file_paths?: string[];
}

export interface PassportSuggestionsResponse
  extends BaseIdentitySuggestionsResponse {
  passport_number?: string;
  country?: string;
  is_country_blacklisted?: boolean | string;
  file_path?: string;
}

export interface BankStatementSuggestionsResponse {
  bsb?: string;
  bank_name?: string;
  account_number?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  statement_date?: Date;
  less_than_3_months_old?: string;
  application_id?: string;
  file_path?: string;
}

export interface ActivationFunctions {
  abortRequests: () => void;
  getActivationData: () => Promise<StoredActivationData | null>;
  patchMerchantApplicationAsync: (
    payload: Record<string, any>
  ) => Promise<ApiOkResponse>;
  getMerchantApplicationUploadUrlAsync: (
    identityDocumentType: IdentityDocumentType,
    fileName: string
  ) => Promise<ApiOkResponse<{ url: string; s3ObjectUri: string }>>;
  getDriversLicenceSuggestionsAsync: (
    filePaths: string[]
  ) => Promise<ApiOkResponse<DriversLicenceSuggestionsResponse>>;
  getPassportSuggestionsAsync: (
    filePath: string
  ) => Promise<ApiOkResponse<PassportSuggestionsResponse>>;
  getBankStatementSuggestionsAsync: (
    filePath: string
  ) => Promise<ApiOkResponse<BankStatementSuggestionsResponse>>;
  getApplicationFormOptionsAsync: () => Promise<ApplicationFormOptionsMap>;
  getApplicationProgressAsync: () => Promise<
    ApiOkResponse<MerchantApplicationProgressResponse>
  >;
  patchApplicationProgressAsync: (
    payload: MerchantApplicationProgressResponse
  ) => Promise<ApiOkResponse>;
  submitMerchantApplicationAsync: () => Promise<ApiOkResponse>;
  mapServerError: (err: any) => MappedError;
}

export const useActivationFunctions = (): ActivationFunctions => {
  const { get, patch, post, put } = useFetch();
  const abortControllerRef = useRef(new AbortController());

  const abortRequests = useCallback((): void => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
  }, []);

  const getApplicationAsync = useCallback(async (): Promise<
    ApiOkResponse<MerchantApplicationResponse>
  > => {
    const res = await get(
      '/MerchantApplications',
      abortControllerRef.current.signal
    );
    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse<MerchantApplicationResponse>;
    }

    throwApiError(res, body);
  }, [get]);

  const getApplicationProgressAsync = useCallback(async (): Promise<
    ApiOkResponse<MerchantApplicationProgressResponse>
  > => {
    const res = await get(
      `/MerchantApplications/application-progress`,
      abortControllerRef.current.signal
    );
    const body = await readBodySafe(res);

    if (res.ok) {
      return {
        success: true,
        ...body,
      } as ApiOkResponse<MerchantApplicationProgressResponse>;
    }

    throwApiError(res, body);
  }, [get]);

  const patchMerchantApplicationAsync = useCallback(
    async (payload: Record<string, any>): Promise<ApiOkResponse> => {
      const res = await patch(
        '/MerchantApplications',
        payload,
        abortControllerRef.current.signal
      );
      const body = await readBodySafe(res);

      if (res.ok) {
        return {
          success: true,
          ...body,
        } as ApiOkResponse;
      }

      throwApiError(res, body);
    },
    [patch]
  );

  const getMerchantApplicationUploadUrlAsync = useCallback(
    async (
      identityDocumentType: IdentityDocumentType,
      fileName: string
    ): Promise<ApiOkResponse<{ url: string; s3ObjectUri: string }>> => {
      const res = await get(
        `/MerchantApplications/${identityDocumentType}-documents/${encodeURIComponent(
          fileName
        )}/upload-url`,
        abortControllerRef.current.signal
      );
      const body = await readBodySafe(res);

      if (res.ok) {
        return {
          success: true,
          ...body,
        } as ApiOkResponse<{ url: string; s3ObjectUri: string }>;
      }

      throwApiError(res, body);
    },
    [get]
  );

  const getDriversLicenceSuggestionsAsync = useCallback(
    async (
      filePaths: string[]
    ): Promise<ApiOkResponse<DriversLicenceSuggestionsResponse>> => {
      const res = await post(
        '/MerchantApplications/suggestions/drivers-licence',
        {
          // eslint-disable-next-line camelcase
          file_paths: filePaths,
        },
        abortControllerRef.current.signal
      );
      const body = await readBodySafe(res);

      if (res.ok) {
        return {
          success: true,
          ...(body.data ?? {}),
        } as ApiOkResponse<DriversLicenceSuggestionsResponse>;
      }

      throwApiError(res, body);
    },
    [post]
  );

  const getPassportSuggestionsAsync = useCallback(
    async (
      filePath: string
    ): Promise<ApiOkResponse<PassportSuggestionsResponse>> => {
      const res = await post(
        '/MerchantApplications/suggestions/passport',
        {
          // eslint-disable-next-line camelcase
          file_path: filePath,
        },
        abortControllerRef.current.signal
      );
      const body = await readBodySafe(res);

      if (res.ok) {
        return {
          success: true,
          ...(body.data ?? {}),
        } as ApiOkResponse<PassportSuggestionsResponse>;
      }

      throwApiError(res, body);
    },
    [post]
  );

  const getBankStatementSuggestionsAsync = useCallback(
    async (
      filePath: string
    ): Promise<ApiOkResponse<BankStatementSuggestionsResponse>> => {
      const res = await post(
        '/MerchantApplications/suggestions/bank-statement',
        {
          // eslint-disable-next-line camelcase
          file_path: filePath,
        },
        abortControllerRef.current.signal
      );
      const body = await readBodySafe(res);

      if (res.ok) {
        return {
          success: true,
          ...(body.data ?? {}),
        } as ApiOkResponse<BankStatementSuggestionsResponse>;
      }

      throwApiError(res, body);
    },
    [post]
  );

  const patchApplicationProgressAsync = useCallback(
    async (
      payload: MerchantApplicationProgressResponse
    ): Promise<ApiOkResponse> => {
      const res = await patch(
        '/MerchantApplications/application-progress',
        payload,
        abortControllerRef.current.signal
      );
      const body = await readBodySafe(res);

      if (res.ok) {
        return {
          success: true,
          ...body,
        } as ApiOkResponse;
      }

      throwApiError(res, body);
    },
    [patch]
  );

  const getApplicationFormOptionsAsync =
    useCallback(async (): Promise<ApplicationFormOptionsMap> => {
      try {
        const res = await get(
          '/merchantapplications/application-form-options',
          abortControllerRef.current.signal
        );
        const body: ApplicationFormOption[] = await res.json();

        return body.reduce((map, option) => {
          map[option.questionType] = option.candidateAnswers;
          return map;
        }, {} as ApplicationFormOptionsMap);
      } catch (err) {
        if (!isAbortError(err)) {
          logError(PageAction.merchantOnboardingGetFormOptions, err);
        }
        return {};
      }
    }, [get]);

  const submitMerchantApplicationAsync =
    useCallback(async (): Promise<ApiOkResponse> => {
      const res = await put(
        '/MerchantApplications/submit',
        {},
        abortControllerRef.current.signal
      );
      const body = await readBodySafe(res);

      if (res.ok) {
        return { success: true, ...body } as ApiOkResponse;
      }

      throwApiError(res, body);
    }, [put]);

  const getActivationData =
    useCallback(async (): Promise<StoredActivationData | null> => {
      try {
        // Fetch application data and progress in parallel so that the
        // AI-confirmed flags stored on the server are available on any
        // device/browser, not just the one where the step was originally
        // submitted. The progress call is best-effort — a failure does not
        // block the main application data from being returned.
        const [response, progressResponse] = await Promise.all([
          getApplicationAsync(),
          getApplicationProgressAsync().catch(() => null),
        ]);
        if (response) {
          const data: StoredActivationData = {
            applicationId: response.applicationId,
            status: response.status,
            applicant_user_id: response.applicant_user_id,
            applicantUserId: response.applicantUserId,
            signUpInfo: response.signUpInfo,
            businessDetails: response.businessDetails,
            directorDetails: response.directorDetails,
            settlementDetails: response.settlementDetails,
            aiPrefilling: normaliseAIPrefilling(
              response.aiPrefilling as Record<string, unknown>
            ),
            salesforceSdocContractUrl: response.salesforceSdocContractUrl,
            // AI-confirmed flags live server-side on the progress endpoint.
            // Include them in the returned data so the context is the single
            // source of truth — no localStorage required.
            ...(progressResponse?.businessDetailsStepAAIConfirmed != null && {
              businessDetailsStepAAIConfirmed:
                progressResponse.businessDetailsStepAAIConfirmed,
            }),
            ...(progressResponse?.businessDetailsStepBAIConfirmed != null && {
              businessDetailsStepBAIConfirmed:
                progressResponse.businessDetailsStepBAIConfirmed,
            }),
            ...(progressResponse?.businessDetailsStepCAIConfirmed != null && {
              businessDetailsStepCAIConfirmed:
                progressResponse.businessDetailsStepCAIConfirmed,
            }),
          };
          return data;
        }
      } catch (err) {
        if (!isAbortError(err)) {
          console.error('Failed to fetch application status:', err);
        }
      }

      return null;
    }, [getApplicationAsync, getApplicationProgressAsync]);

  return {
    abortRequests,
    getActivationData,
    submitMerchantApplicationAsync,
    getMerchantApplicationUploadUrlAsync,
    getDriversLicenceSuggestionsAsync,
    getPassportSuggestionsAsync,
    getBankStatementSuggestionsAsync,
    getApplicationProgressAsync,
    patchApplicationProgressAsync,
    patchMerchantApplicationAsync,
    getApplicationFormOptionsAsync,
    mapServerError: defaultErrorMapper,
  };
};
